import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendOtp, verifyOtp } from '../../api/api';
import { startSessionLog } from '../../utils/session';
import { bindThisDevice, loadOrganisers, needsPasswordChange, thisDeviceId } from '../../utils/authFlow';

// Reached ONLY when mobile + password were right but the account is bound to a
// different device. A WhatsApp OTP proves ownership; on success the binding moves
// to this device (the old one is signed out by DeviceGuard on its next check).
// The API re-sends an OTP only after the previous one expired (1 hour, Meta bills
// per message) — `reused` carries the Date_Time of the still-valid earlier code.
export function useOtp() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const mobile = state?.mobile;
  const password = state?.password;
  const pendingUser = state?.pendingUser;
  const remember = state?.remember;
  const boundModel = state?.boundModel;
  const boundDate = state?.boundDate;
  const [otp, setOtpRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [reused, setReused] = useState(null);
  // Guard against React StrictMode's double effect-invoke (dev) / re-mounts,
  // which otherwise fires SendOTP twice.
  const otpRequested = useRef(false);

  useEffect(() => {
    if (!mobile || !pendingUser) { nav('/'); return; }
    if (otpRequested.current) return;
    otpRequested.current = true;
    sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' })
      .then(r => { setSent(true); setReused(r?.reused ? r.Date_Time : null); })
      .catch(() => setErr('Could not send OTP'));
  }, [mobile, pendingUser, nav]);

  const resend = async () => {
    setErr('');
    try {
      const r = await sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' });
      setSent(true); setReused(r?.reused ? r.Date_Time : null);
    } catch { setErr('Could not resend OTP'); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^\d{4}$/.test(otp)) { setErr('Enter the 4-digit OTP'); return; }
    setBusy(true);
    try {
      const res = await verifyOtp({ MobileNumber: mobile, OTP: otp });
      if (!res?.verified) { setErr(res?.reason || 'Invalid OTP'); return; }
      const user = await bindThisDevice(pendingUser); // move the binding here
      const list = await loadOrganisers(user);
      if (list.length === 0) { setErr('No organiser assigned'); return; }
      const mustChange = needsPasswordChange(mobile, password);
      login(user, list[0], { mobile, password }, remember, mustChange);
      startSessionLog(user, await thisDeviceId()); // open activity-log session (best-effort)
      nav(mustChange ? '/change-password' : '/app/dashboard', { replace: true });
    } catch (e2) {
      setErr(e2?.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const setOtp = (v) => setOtpRaw(String(v || '').replace(/\D/g, '').slice(0, 4));
  const backToLogin = () => nav('/');

  return { mobile, boundModel, boundDate, otp, setOtp, busy, err, sent, reused, resend, verify, backToLogin };
}
